import React from 'react';

import { useExperimentGatedAppTakeover } from 'features/app-takeover/hooks/use-app-takeover';
import { useMaybePlayAudioOnceOnMount } from 'features/app-takeover/utils/play-audio-once';
import { MenuReorderWidget } from 'features/menu/components/menu-reorder-widget';
import MenuTileGrid from 'features/menu/components/menu-tile-grid';
import { MenuSkeletonLoader } from 'features/menu/components/menu-tile-grid/menu-tile-grid-loading';
import withMenuEmptyState from 'features/menu/components/with-menu-empty-state';
import { useFetchMainMenu, useFetchMainMenuOnMount } from 'features/menu/hooks/use-fetch-main-menu';
import { useMenuScroll } from 'features/menu/hooks/use-menu-scroll';
import { MenuEmptyState } from 'features/menu/pages/main-menu-page/menu-empty-state';
import { excludeDisabledMenuSections } from 'features/menu/pages/main-menu-page/utils';
import { useStoreContext } from 'features/store';

import { IMainMenuViewOption } from '@rbi-ctg/menu';
import { Helmet } from 'components/helmet';
import { usePullToReload } from 'components/pull-to-reload/pull-to-reload';
import { t } from 'state/intl/translations';
import { useMenuContext } from 'state/menu';
import { useServiceModeLive } from 'state/service-mode';
import { waitForDuration } from 'utils/wait-for-duration';

// OLD legacy menu
const MainMenuComponent = () => {
  const fetchMainMenu = useFetchMainMenu();
  const { store } = useStoreContext();

  const serviceMode = useServiceModeLive();
  const {
    activeDayParts,
    allPricingDependenciesAreSettled,
    forceRefreshActiveDayParts,
    showBurgersForBreakfast,
    hasError,
    data,
    getPricingAndAvailability,
    isMenuLoading,
  } = useMenuContext();

  const menuData = excludeDisabledMenuSections({
    menuData: data?.Menu?.options as IMainMenuViewOption[] | undefined,
    activeDayParts,
    showBurgersForBreakfast,
    serviceMode,
    getPricingAndAvailability,
  });

  const onRefresh = async (): Promise<void> => {
    // This is just an animation feedback of one second
    await waitForDuration(1000);
    forceRefreshActiveDayParts();
    fetchMainMenu(true);
  };

  useMenuScroll();

  const { isPosDataLoading } = useStoreContext();
  const isFetchMainMenuPending = useFetchMainMenuOnMount();
  const loading =
    isFetchMainMenuPending || isMenuLoading || isPosDataLoading || (!data && !hasError);

  const hasMenuData = !!menuData?.length && allPricingDependenciesAreSettled;

  const pullToReloadProps = usePullToReload({
    onRefresh,
    isDisabled: loading,
  });

  useMaybePlayAudioOnceOnMount(useExperimentGatedAppTakeover()?.menu?.audio, {
    shouldPlay: hasMenuData && !hasError,
    globalPlayOnce: true,
  });

  const menuContent =
    hasMenuData && !hasError ? (
      <MenuTileGrid data={menuData} {...pullToReloadProps} />
    ) : (
      <MenuEmptyState
        onRefresh={onRefresh}
        logWhenTrue={!loading}
        logContext={{
          menuType: 'legacy',
          activeDayParts: activeDayParts.map(dayPart => dayPart.key),
          showBurgersForBreakfast,
          serviceMode,
          isFetchedMenuEmpty: !hasMenuData,
          originalMenuDataLength: data?.Menu?.options?.length,
          finalMenuDataLength: menuData?.length,
          isPricingAndDependenciesSettled: allPricingDependenciesAreSettled,
          storeId: store.number,
          storePosVendor: store.pos?.vendor,
          hasError,
          isMenuLoading,
        }}
      />
    );

  return (
    <>
      <Helmet title={t('mainMenu')} />
      {loading ? (
        <MenuSkeletonLoader />
      ) : (
        <>
          <MenuReorderWidget />
          {menuContent}
        </>
      )}
    </>
  );
};

// MainMenu.whyDidYouRender = true;
export const MainMenuPage = withMenuEmptyState(MainMenuComponent);
